import React from "react";
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { T, cardSx, labelSx, chartTooltipStyle, axisTick, StatTile, SectionLabel, EmptyNote } from "./PlatformAnalyticsShared";
import FunnelChart from "./FunnelChart";

const SOURCE_SERIES = [
  { key: "talent", name: "Talent Team", color: T.primary },
  { key: "cvtheque", name: "CV Library", color: T.blue },
  { key: "organic", name: "Organic", color: T.dim },
];

const headCellSx = {
  borderBottom: `1px solid ${T.border}`,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: T.dim,
  py: "10px",
  px: "16px",
};

const bodyCellSx = {
  py: "12px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: T.fg,
  borderBottom: `1px solid ${T.border}`,
};

const TalentTeamTab = ({ data }) => {
  const {
    source_share: sourceShare = [],
    win_rates: winRates = [],
    outcome_funnel: outcomeFunnel = [],
    leaderboard,
    denial_reasons: denialReasons = [],
    time_to_decision: ttd,
  } = data;

  const lbRows = leaderboard?.rows || [];
  const coverageNote = leaderboard
    ? `based on ${leaderboard.attributed} of ${leaderboard.total} talent-sent apps with a recorded creator`
    : null;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
        {winRates.map((w) => (
          <StatTile key={w.source} label={`${w.source} hired rate`} value={w.rate} suffix="%" />
        ))}
        <StatTile label="Talent time-to-decision" value={ttd?.talent ?? "—"} suffix={ttd?.talent != null ? "d" : ""} />
      </Box>

      <Box sx={cardSx}>
        <Typography sx={{ ...labelSx, mb: 1.5 }}>Application source share (last 12 months)</Typography>
        {sourceShare.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sourceShare} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
              <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif" }} />
              {SOURCE_SERIES.map((s) => (
                <Bar key={s.key} dataKey={s.key} name={s.name} stackId="src" fill={s.color} maxBarSize={36} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyNote />
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: "16px" }}>
        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 1.5 }}>Hired rate by source</Typography>
          {winRates.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={winRates} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
                <XAxis dataKey="source" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [`${v}%`, "Hired rate"]} />
                <Bar dataKey="rate" name="Hired rate" fill={T.green} radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote />
          )}
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 2 }}>Talent-sent outcome funnel</Typography>
          <FunnelChart stages={outcomeFunnel} color={T.primary} />
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: "16px" }}>
        <Box>
          <SectionLabel note={coverageNote}>Team member effectiveness</SectionLabel>
          <Box sx={{ ...cardSx, p: 0, overflow: "hidden", mt: 1 }}>
            {lbRows.length === 0 ? (
              <EmptyNote>No attributed talent-sent applications</EmptyNote>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Team member", "Sent", "Accepted", "Rate"].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lbRows.map((r) => (
                    <TableRow key={r.internal_user_id} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                      <TableCell sx={{ ...bodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{r.full_name}</TableCell>
                      <TableCell sx={bodyCellSx}>{r.sent}</TableCell>
                      <TableCell sx={bodyCellSx}>{r.accepted}</TableCell>
                      <TableCell sx={bodyCellSx}>{r.acceptance_rate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 1.5 }}>Denial reasons (talent-sent)</Typography>
          {denialReasons.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(160, denialReasons.length * 44)}>
              <BarChart data={denialReasons} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="label" width={130} tick={axisTick} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" name="Denials" fill={T.red} radius={[0, 4, 4, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote>No denial reasons recorded</EmptyNote>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default TalentTeamTab;
